import get from "lodash/get";

/**
 * Evaluates a condition object against react-final-form's `values`. Supports
 * nested rule groups combined with AND/OR.
 *
 * Condition shape:
 *   {
 *     operator: "AND" | "OR",
 *     rules: [
 *       { field: "some.nested[0].path", operator: "==", value: "foo" },
 *       { operator: "OR", rules: [...] },
 *     ],
 *   }
 */
export const rffEvaluateFieldCondition = (condition, values) => {
  if (!condition || !condition.rules) return true;

  const { operator, rules } = condition;

  const evaluateRule = (rule) => {
    if (rule.rules) return rffEvaluateFieldCondition(rule, values);

    const fieldValue = get(values, rule.field);
    return evaluateCondition(fieldValue, rule.operator, rule.value);
  };

  const results = rules.map(evaluateRule);

  return operator === "AND" ? results.every(Boolean) : results.some(Boolean);
};

const evaluateCondition = (fieldValue, operator, value) => {
  switch (operator) {
    case "==":
      return fieldValue === value;
    case "!=":
      return fieldValue !== value;
    case ">":
      return fieldValue > value;
    case "<":
      return fieldValue < value;
    case ">=":
      return fieldValue >= value;
    case "<=":
      return fieldValue <= value;
    // "includes" reads in whichever direction makes sense: a multi-select field
    // containing `value`, or a scalar field appearing in a `value` list.
    case "includes":
      if (Array.isArray(fieldValue)) return fieldValue.includes(value);
      return Array.isArray(value) ? value.includes(fieldValue) : false;
    case "excludes":
      if (Array.isArray(fieldValue)) return !fieldValue.includes(value);
      return Array.isArray(value) ? !value.includes(fieldValue) : false;
    // "has the user filled this in yet" — `!=` with "" can't express it, since
    // an untouched field is undefined and `undefined !== ""` is true.
    case "notEmpty":
      return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
    case "isEmpty":
      return fieldValue === undefined || fieldValue === null || fieldValue === "";
    default:
      return false;
  }
};

export default rffEvaluateFieldCondition;
